// src/pages/EmailTemplates.jsx
import React, {
    useEffect,
    useMemo,
    useState,
    useCallback,
    useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import UnsavedDialog from "../components/UnsavedDialog";
import axiosInstance from "../axiosInstance";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";

/* ---- Quill: tailles (px) ---- */
const SizeStyle = Quill.import("attributors/style/size");
SizeStyle.whitelist = ["12px", "14px", "16px", "18px", "24px", "32px"];
Quill.register(SizeStyle, true);
const SIZE_OPTIONS = ["12px", "14px", "16px", "18px", "24px", "32px"];

/* ---- Types ---- */
const TEMPLATES = [
    { id: "devis", label: "Envoi de devis" },
    { id: "facture", label: "Envoi de facture" },
    { id: "relance_devis", label: "Relance de devis" },
    { id: "relance_impaye", label: "Relance impayé" },
    // Nouveaux modèles facture
    { id: "facture_acompte", label: "Acompte" },
    { id: "facture_partielle", label: "Partielle" },
    { id: "facture_finale", label: "Finale" },
];

/* ---- Defaults ---- */
const DEFAULTS = {
    devis: {
        subject: "Votre devis #{DOC_NUMBER} – {COMPANY_NAME}",
        html: `<p>Bonjour {CLIENT_NAME},</p>
<p>Veuillez trouver ci-joint votre devis <strong>{DOC_NUMBER}</strong> d’un montant de <strong>{DOC_TOTAL}</strong>.</p>

<p>Restant à votre disposition,</p>
<p>{COMPANY_NAME}<br/>{COMPANY_PHONE} — {COMPANY_EMAIL}</p>`,
    },
    facture: {
        subject: "Votre facture #{DOC_NUMBER} – {COMPANY_NAME}",
        html: `<p>Bonjour {CLIENT_NAME},</p>
<p>Veuillez trouver ci-joint votre facture <strong>{DOC_NUMBER}</strong> pour un montant de <strong>{DOC_TOTAL}</strong>.</p>

<p>Cordialement,</p>
<p>{COMPANY_NAME}<br/>{COMPANY_PHONE} — {COMPANY_EMAIL}</p>`,
    },
    relance_devis: {
        subject: "Relance – Devis #{DOC_NUMBER}",
        html: `<p>Bonjour {CLIENT_NAME},</p>
<p>Un petit rappel concernant le devis <strong>{DOC_NUMBER}</strong> envoyé récemment (montant : <strong>{DOC_TOTAL}</strong>).</p>

<p>Nous restons disponibles pour toute question.</p>
<p>{COMPANY_NAME}</p>`,
    },
    relance_impaye: {
        subject: "Relance de paiement – Facture #{DOC_NUMBER}",
        html: `<p>Bonjour {CLIENT_NAME},</p>
<p>Sauf erreur de notre part, la facture <strong>{DOC_NUMBER}</strong> (montant <strong>{DOC_TOTAL}</strong>) reste en attente. Échéance : <strong>{DUE_DATE}</strong>.</p>

<p>Merci de votre règlement,</p>
<p>{COMPANY_NAME}</p>`,
    },
    facture_acompte: {
        subject: "Votre facture d’acompte #{DOC_NUMBER} – {COMPANY_NAME}",
        html: `<p>Bonjour {CLIENT_NAME},</p>
<p>Veuillez trouver ci-joint votre <strong>facture d’acompte</strong> <strong>{DOC_NUMBER}</strong> d’un montant de <strong>{DOC_TOTAL}</strong>.</p>

<p>Merci de votre confiance,</p>
<p>{COMPANY_NAME}<br/>{COMPANY_PHONE} — {COMPANY_EMAIL}</p>`,
    },
    facture_partielle: {
        subject: "Votre facture partielle #{DOC_NUMBER} – {COMPANY_NAME}",
        html: `<p>Bonjour {CLIENT_NAME},</p>
<p>Veuillez trouver ci-joint votre <strong>facture partielle</strong> <strong>{DOC_NUMBER}</strong> d’un montant de <strong>{DOC_TOTAL}</strong>.</p>

<p>Restant à votre disposition,</p>
<p>{COMPANY_NAME}<br/>{COMPANY_PHONE} — {COMPANY_EMAIL}</p>`,
    },
    facture_finale: {
        subject: "Votre facture finale #{DOC_NUMBER} – {COMPANY_NAME}",
        html: `<p>Bonjour {CLIENT_NAME},</p>
<p>Veuillez trouver ci-joint votre <strong>facture finale</strong> <strong>{DOC_NUMBER}</strong> pour un montant de <strong>{DOC_TOTAL}</strong>.</p>

<p>Cordialement,</p>
<p>{COMPANY_NAME}<br/>{COMPANY_PHONE} — {COMPANY_EMAIL}</p>`,
    },
};

/* ---- Variables ---- */
const VARS = [
    ["{CLIENT_NAME}", "Nom du client"],
    ["{DOC_NUMBER}", "Numéro document"],
    ["{DOC_TOTAL}", "Montant TTC"],
    ["{DUE_DATE}", "Échéance"],
    ["{COMPANY_NAME}", "Nom société"],
    ["{COMPANY_PHONE}", "Téléphone société"],
    ["{COMPANY_EMAIL}", "Email société"],
];

/* ---- Aperçu ---- */
const SAMPLE = {
    CLIENT_NAME: "Mme Dupont",
    DOC_NUMBER: "DEV-2025-0012",
    DOC_TOTAL: "1 240,00 €",
    DUE_DATE: "15/10/2025",
    PORTAL_LINK: "https://app.flowdevis.com/…",
    COMPANY_NAME: "MEISSNER PLOMBERIE",
    COMPANY_PHONE: "06 79 45 29 29",
    COMPANY_EMAIL: "contact@societe.com",
};

const A4_WIDTH = 794;
function ConfirmRestoreModal({ open, onCancel, onConfirm }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[2000] grid place-items-center bg-sky-800/40">
            <div className="bg-white rounded-xl shadow-xl w-[520px] max-w-[95vw] overflow-hidden">
                <div className="px-5 pt-4 pb-3 border-b flex items-start gap-3">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" /></svg>
                    </span>
                    <div>
                        <h3 className="text-[15px] font-semibold text-slate-900">Restaurer le modèle ?</h3>
                        <p className="mt-0.5 text-[13px] text-slate-600">
                            Le sujet et le contenu seront réinitialisés aux valeurs par défaut. Cette action est irréversible.
                        </p>
                    </div>
                </div>
                <div className="px-5 py-3 flex flex-col sm:flex-row sm:justify-end gap-2">
                    <button onClick={onCancel}
                        className="h-8 px-3 text-[13px] rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
                        Annuler
                    </button>
                    <button onClick={onConfirm}
                        className="h-8 px-3 text-[13px] rounded-md bg-rose-600 text-white hover:bg-rose-500 shadow-sm">
                        Restaurer
                    </button>
                </div>
            </div>
        </div>
    );
}

function TestEmailModal({
    open, onClose, defaultTo, subject, html, logoSrc, useHeader, useFooter, onSend,
    busy = false,
}) {
    const [to, setTo] = React.useState(defaultTo || "");
    const [cc, setCc] = React.useState("");
    const [err, setErr] = React.useState("");

    React.useEffect(() => {
        if (open) { setTo(defaultTo || ""); setCc(""); setErr(""); }
    }, [open, defaultTo]);

    const validEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());
    const canSend = validEmail(to) && !busy;

    return !open ? null : (
        <div className="fixed inset-0 z-[2000] grid place-items-center bg-black/40">
            <div className="bg-white rounded-xl shadow-xl p-5 w-[720px] max-w-[95vw]">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold">Envoyer un e-mail de test</h3>
                    <button className="text-slate-500 hover:text-slate-900" onClick={onClose} title="Fermer">✕</button>
                </div>

                <div className="grid grid-cols-2 gap-6">
                    {/* Formulaire */}
                    <div>
                        <label className="block text-sm mb-1">À</label>
                        <input
                            className="w-full border rounded px-3 py-2 mb-3"
                            placeholder="nom@domaine.com"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                        />
                        <label className="block text-sm mb-1">Cc (optionnel)</label>
                        <input
                            className="w-full border rounded px-3 py-2 mb-3"
                            placeholder="copie@domaine.com"
                            value={cc}
                            onChange={(e) => setCc(e.target.value)}
                        />
                        {err && <div className="text-xs text-rose-600 mb-2">{err}</div>}

                        <div className="flex gap-2 mt-2">
                            <button
                                disabled={!canSend}
                                onClick={() => {
                                    if (!validEmail(to)) { setErr("Adresse e-mail invalide"); return; }
                                    onSend({ to: to.trim(), cc: cc.trim() || undefined, subject, html, logoSrc, useHeader, useFooter });
                                }}
                                className={`px-4 py-2 rounded text-white ${canSend ? "bg-black hover:bg-slate-800" : "bg-slate-400 cursor-not-allowed"}`}
                            >
                                {busy ? "Envoi…" : "Envoyer le test"}
                            </button>
                            <button className="px-3 py-2 border rounded" onClick={onClose}>Annuler</button>
                        </div>
                    </div>

                    {/* Aperçu minimal */}
                    <div className="border rounded p-3 bg-slate-50 overflow-auto">
                        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Aperçu</div>
                        <div className="text-sm font-semibold mb-2">{subject || "(sans objet)"}</div>
                        <div className="prose prose-sm max-w-none">
                            {logoSrc && useHeader && (
                                <div className="mb-2"><img src={logoSrc} alt="logo" className="max-h-16 object-contain" /></div>
                            )}
                            <div className="ql-editor !p-0" dangerouslySetInnerHTML={{ __html: html || "" }} />
                            {useFooter && <p className="text-[11px] text-slate-500 mt-4">— Email envoyé via FlowDevis</p>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function EmailTemplates({ onDirtyChange = () => { }, setSaveHandler = () => { } }) {
    const navigate = useNavigate();

    /* ---------- état ---------- */
    const [active, setActive] = useState(TEMPLATES[0].id);
    const [data, setData] = useState(() => {
        const d = {};
        TEMPLATES.forEach(({ id }) => {
            d[id] = { ...DEFAULTS[id], logoSrc: null, useHeader: true, useFooter: true };
        });
        return d;
    });
    const [showRestore, setShowRestore] = useState(false);

    const [saving, setSaving] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    const [unsavedOpen, setUnsavedOpen] = useState(false);
    const nextHrefRef = useRef(null);

    /* ---------- refs/guards (doivent être déclarés AVANT tout usage) ---------- */
    const hasLoadedRef = useRef(false);
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    const baselineRef = useRef(null);
    const lastDirtyRef = useRef(false);
    const dataRef = useRef(data);
    useEffect(() => { dataRef.current = data; }, [data]);

    // baseline provisoire (sera écrasée par markBaseline après hydratation)
    useEffect(() => {
        if (!baselineRef.current) {
            baselineRef.current = JSON.stringify(data);
        }

    }, []);

    const saveRef = useRef(async () => { });

    // isDirty = data modifié vs baseline
    const isDirty = useMemo(() => {
        if (!baselineRef.current) return false;
        return JSON.stringify(data) !== baselineRef.current;
    }, [data]);

    useEffect(() => {
        const handler = (e) => {
            if (!isDirty) return;
            e.preventDefault();
            e.returnValue = "";
            return "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [isDirty]);

    const requestLeaveTo = useCallback((to) => {
        if (!isDirty) {
            navigate(to);
            return;
        }
        nextHrefRef.current = to;
        setUnsavedOpen(true);
    }, [isDirty, navigate]);

    // Interception globale des liens internes (sidebar, header, etc.)
    useEffect(() => {
        if (!isDirty) return;
        const onClick = (e) => {
            // chercher <a href>
            let el = e.target;
            while (el && el !== document.body) {
                if (el.tagName === "A" && el.href) break;
                el = el.parentElement;
            }
            if (!el || el.tagName !== "A") return;
            const href = el.getAttribute("href");
            const target = el.getAttribute("target");
            if (!href || href.startsWith("#") || target === "_blank") return;
            const url = new URL(el.href);
            // On n’intercepte que les liens internes
            if (url.origin !== window.location.origin) return;
            e.preventDefault();
            e.stopPropagation();
            nextHrefRef.current = url.pathname + url.search + url.hash;
            setUnsavedOpen(true);
        };
        document.addEventListener("click", onClick, true); // capture
        return () => document.removeEventListener("click", onClick, true);
    }, [isDirty]);

    // Bloque les navigations back/forward si dirty
    useEffect(() => {
        if (!isDirty) return;
        // priming de l’historique pour pouvoir "canceler"
        try { history.pushState({ __guard: true }, ""); } catch { }
        const onPop = (e) => {
            // Empêche la sortie immédiate
            try { history.pushState({ __guard: true }, ""); } catch { }
            nextHrefRef.current = document.referrer || "/"; // valeur par défaut
            setUnsavedOpen(true);
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, [isDirty]);

    const onUnsavedCancel = useCallback(() => {
        setUnsavedOpen(false);
        nextHrefRef.current = null;
    }, []);

    const onUnsavedExit = useCallback(() => {
        const target = nextHrefRef.current || "/personnaliser?tab=emails";
        setUnsavedOpen(false);
        nextHrefRef.current = null;
        navigate(target);
    }, [navigate]);

    const onUnsavedSaveAndExit = useCallback(async () => {
        try {
            await saveRef.current?.(); // déclenche l’enregistrement
        } finally {
            const target = nextHrefRef.current || "/personnaliser?tab=emails";
            setUnsavedOpen(false);
            nextHrefRef.current = null;
            navigate(target);
        }
    }, [navigate]);

    /* ---------- quill ---------- */
    const quillRef = useRef(null);
    const quillModules = useMemo(
        () => ({ toolbar: { container: "#toolbar-email-template" }, clipboard: { matchVisual: false } }),
        []
    );
    const quillFormats = useMemo(
        () => ["size", "bold", "italic", "underline", "align", "list", "bullet", "link", "clean"],
        []
    );

    /* ---------- helpers ---------- */
    const current = data[active] || { subject: "", html: "", logoSrc: null, useHeader: true, useFooter: true };
    const setCurrent = (patch) => setData((prev) => ({ ...prev, [active]: { ...(prev[active] || {}), ...patch } }));

    const replaceVars = useCallback(
        (raw = "") => Object.entries(SAMPLE).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, v), raw),
        []
    );

    const insertVarIntoSubject = (token) => {
        const s = current.subject || "";
        const needsSpace = s !== "" && !s.endsWith(" ");
        setCurrent({ subject: (s + (needsSpace ? " " : "") + token).trim() });
    };

    const insertVarIntoEditor = (token) => {
        const quill = quillRef.current?.getEditor?.();
        if (!quill) return;
        const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
        quill.insertText(range.index, token);
        quill.setSelection(range.index + token.length, 0);
    };

    const markBaseline = useCallback(
        (snapshot) => {
            baselineRef.current = JSON.stringify(snapshot || {});
            lastDirtyRef.current = false;
            onDirtyChange(false);
        },
        [onDirtyChange]
    );

    /* ---------- dirty ---------- */
    useEffect(() => {
        if (!baselineRef.current) return;
        const isDirty = JSON.stringify(data) !== baselineRef.current;
        if (isDirty !== lastDirtyRef.current) {
            lastDirtyRef.current = isDirty;
            onDirtyChange(isDirty);
        }
    }, [data, onDirtyChange]);

    /* ---------- save handler ---------- */
    useEffect(() => {
        saveRef.current = async () => {
            try {
                setSaving(true);
                await axiosInstance.post("/email-templates", dataRef.current);
                if (!isMounted.current) return;
                markBaseline(dataRef.current);
                alert("✅ Modèles d’e-mails enregistrés.");
            } catch (e) {
                console.error(e);
                alert("❌ Erreur lors de l’enregistrement.");
            } finally {
                if (isMounted.current) setSaving(false);
            }
        };
    }, [markBaseline]);

    useEffect(() => {
        setSaveHandler(() => () => saveRef.current());
        return () => setSaveHandler(null);
    }, [setSaveHandler]);

    /* ---------- hydrate (1 fois) ---------- */
    useEffect(() => {
        if (hasLoadedRef.current) return;
        hasLoadedRef.current = true;
        (async () => {
            try {
                const [entRes, tplRes] = await Promise.allSettled([
                    axiosInstance.get("/entreprise"),
                    axiosInstance.get("/email-templates"),
                ]);
                const ent = entRes.status === "fulfilled" ? entRes.value?.data : null;
                const loaded = tplRes.status === "fulfilled" ? (tplRes.value?.data || {}) : {};
                const merged = {};
                TEMPLATES.forEach(({ id }) => {
                    merged[id] = {
                        subject: loaded[id]?.subject || DEFAULTS[id].subject,
                        html: (loaded[id]?.html || DEFAULTS[id].html).trim(),
                        logoSrc: loaded[id]?.logoSrc ?? ent?.logo_url ?? null,
                        useHeader: Boolean(loaded[id]?.useHeader ?? true),
                        useFooter: Boolean(loaded[id]?.useFooter ?? true),
                    };
                });
                if (!isMounted.current) return;
                setData(merged);
                setLogoPreview((merged[active] || {}).logoSrc || null);
                markBaseline(merged);
            } catch (e) {
                console.warn("Hydratation templates échouée (defaults gardés):", e?.message);
                const fallback = {};
                TEMPLATES.forEach(({ id }) => {
                    fallback[id] = { ...DEFAULTS[id], logoSrc: null, useHeader: true, useFooter: true };
                });
                if (!isMounted.current) return;
                setData(fallback);
                setLogoPreview(null);
                markBaseline(fallback);
            }
        })();
    }, [active, markBaseline]);

    /* ---------- UI ---------- */
    const restoreDefaults = () => {
        if (!window.confirm("Restaurer le sujet et le contenu par défaut pour ce modèle ?")) return;
        setCurrent({ subject: DEFAULTS[active].subject, html: DEFAULTS[active].html });
    };

    const handleLogoUpload = async (file) => {
        if (!file) return;
        const blob = URL.createObjectURL(file);
        setLogoPreview(blob);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await axiosInstance.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setCurrent({ logoSrc: r.data?.url || null });
        } catch {
            setCurrent({ logoSrc: null });
        }
    };

    return (
        <div className="p-4 space-y-5">
            {/* Titre */}
            <h1 className="text-2xl font-bold text-slate-800">Personnaliser</h1>

            {/* Header/onglets haut */}
            <div className="bg-white rounded-xl border px-4 py-2">
                <div className="flex items-center gap-3">
                    <div className="w-1" />
                    <div className="flex-1 flex items-center justify-center">
                        <div className="inline-flex items-center rounded-full border border-slate-300 overflow-hidden">
                            <button
                                type="button"
                                className="px-4 h-9 text-sm bg-white text-slate-700"
                                onClick={() => requestLeaveTo("/personnaliser?tab=societe")}
                            >
                                Informations société
                            </button>
                            <button
                                type="button"
                                className="px-4 h-9 text-sm border-l border-slate-300 bg-white text-slate-700"
                                onClick={() => requestLeaveTo("/personnaliser?tab=footer")}
                            >
                                Pied de page
                            </button>
                            <button
                                type="button"
                                className="px-4 h-9 text-sm border-l border-slate-300 bg-white text-slate-700"
                                onClick={() => requestLeaveTo("/personnaliser?tab=mentions")}
                            >
                                Mentions légales
                            </button>
                            <button
                                type="button"
                                className="px-4 h-9 text-sm border-l border-slate-300 bg-gray-300 text-black"
                                aria-pressed="true"
                            >
                                Modèles d’e-mails
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <UnsavedDialog
                open={unsavedOpen}
                onCancel={onUnsavedCancel}
                onExit={onUnsavedExit}
                onSaveAndExit={onUnsavedSaveAndExit}
            />
            {/* Rappels & variables */}
            <div className="bg-white rounded-xl border p-4">
                <div className="text-sm font-medium text-slate-800">Rappels & variables</div>
                <div className="text-xs text-slate-500">
                    Utilisez ces variables dans le <strong>sujet</strong> ou le <strong>contenu</strong>. Elles seront remplacées automatiquement (voir l’aperçu).
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    {VARS.map(([key, label]) => (
                        <span
                            key={key}
                            className="text-[12px] px-2 h-7 inline-flex items-center rounded-full border border-slate-300 bg-slate-50"
                            title={label}
                        >
                            <code className="mr-1">{key}</code>
                            <span className="text-slate-500">{label}</span>
                        </span>
                    ))}
                </div>
            </div>

            {/* Corps */}
            <div className="bg-white rounded-xl border p-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                    {/* ÉDITEUR */}
                    <div className="bg-white border rounded-xl shadow-sm">
                        <div className="p-4 border-b">
                            <div className="grid grid-cols-12 gap-3">
                                <div className="col-span-12">
                                    <label className="block text-sm font-medium text-slate-800 mb-1">Sujet</label>
                                    <input
                                        type="text"
                                        value={current.subject}
                                        onChange={(e) => setCurrent({ subject: e.target.value })}
                                        className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                                        placeholder="Sujet de l’e-mail…"
                                    />
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {VARS.map(([key, label]) => (
                                            <button
                                                key={key}
                                                type="button"
                                                className="text-[12px] px-2 h-7 rounded-full border border-slate-300 bg-white hover:bg-slate-100"
                                                onClick={() => insertVarIntoSubject(key)}
                                                title={label}
                                            >
                                                {key}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="col-span-12 flex items-center gap-4">
                                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                                        <input
                                            type="checkbox"
                                            checked={current.useHeader}
                                            onChange={(e) => setCurrent({ useHeader: e.target.checked })}
                                        />
                                        Inclure l’en-tête société (si configuré)
                                    </label>
                                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                                        <input
                                            type="checkbox"
                                            checked={current.useFooter}
                                            onChange={(e) => setCurrent({ useFooter: e.target.checked })}
                                        />
                                        Inclure le pied de page
                                    </label>
                                </div>

                                <div className="col-span-12">
                                    <div className="text-sm text-slate-800 mb-1">Contenu</div>
                                    <div id="toolbar-email-template" className="ql-toolbar ql-snow rounded-t-md">
                                        <select className="ql-size" defaultValue="">
                                            <option value="">Défaut</option>
                                            {SIZE_OPTIONS.map((v) => (
                                                <option key={v} value={v}>
                                                    {parseInt(v, 10)}
                                                </option>
                                            ))}
                                        </select>
                                        <button className="ql-bold" />
                                        <button className="ql-italic" />
                                        <button className="ql-underline" />
                                        <select className="ql-align" />
                                        <button className="ql-list" value="ordered" />
                                        <button className="ql-list" value="bullet" />
                                        <button className="ql-link" />
                                        <button className="ql-clean" />
                                    </div>
                                    <ReactQuill
                                        ref={quillRef}
                                        theme="snow"
                                        value={current.html}
                                        onChange={(html) => setCurrent({ html })}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        className="rounded-b-md bg-white"
                                        placeholder="Composez votre message (variables, liens, mise en forme)…"
                                    />
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {VARS.map(([key, label]) => (
                                            <button
                                                key={key}
                                                type="button"
                                                className="text-[12px] px-2 h-7 rounded-full border border-slate-300 bg-white hover:bg-slate-100"
                                                onClick={() => insertVarIntoEditor(key)}
                                                title={label}
                                            >
                                                {key}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="col-span-12">
                                    <div className="flex items-center gap-3">
                                        <div className="text-sm text-slate-800">Logo</div>
                                        <label className="px-3 h-8 inline-flex items-center rounded border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm cursor-pointer">
                                            Importer un logo
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={(e) => {
                                                    const f = e.target.files?.[0];
                                                    if (f) handleLogoUpload(f);
                                                    e.target.value = "";
                                                }}
                                            />
                                        </label>
                                        {logoPreview ? (
                                            <div className="ml-2 border rounded bg-white shadow-sm p-1">
                                                <img
                                                    src={logoPreview}
                                                    alt="logo"
                                                    className="max-h-10 object-contain"
                                                    style={{ maxWidth: 160 }}
                                                />
                                            </div>
                                        ) : (
                                            <span className="text-xs text-slate-500">Aucun</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* APERÇU */}
                    <div className="bg-white border rounded-xl shadow-sm">
                        <div className="p-4 border-b space-y-2">
                            <div className="text-sm text-slate-800 font-medium">Aperçu (exemple)</div>
                            <div className="text-xs text-slate-500">Tel que l’utilisateur le verra dans son client e-mail.</div>
                            <div className="flex items-center gap-4">
                                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={current.useHeader}
                                        onChange={(e) => setCurrent({ useHeader: e.target.checked })}
                                    />
                                    Inclure l’en-tête société
                                </label>
                                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={current.useFooter}
                                        onChange={(e) => setCurrent({ useFooter: e.target.checked })}
                                    />
                                    Inclure le pied de page
                                </label>
                            </div>
                        </div>

                        <div className="p-4">
                            <div className="border rounded-lg overflow-hidden max-w-[760px] mx-auto">
                                <div className="px-4 py-3 border-b bg-slate-50">
                                    <div className="text-[13px]">
                                        <span className="text-slate-500 mr-1">Objet :</span>
                                        <span className="font-medium">{replaceVars(current.subject || "(sans objet)")}</span>
                                    </div>
                                </div>

                                <div className="p-5">
                                    {logoPreview && current.useHeader && (
                                        <div className="mb-3">
                                            <img
                                                src={logoPreview}
                                                alt="logo"
                                                className="max-h-40 object-contain"
                                                style={{ maxWidth: A4_WIDTH }}
                                            />
                                        </div>
                                    )}

                                    <div
                                        className="ql-editor !p-0"
                                        dangerouslySetInnerHTML={{ __html: replaceVars(current.html || "") }}
                                    />

                                    {current.useFooter && (
                                        <p className="text-[11px] text-slate-500 mt-6">— Email envoyé via FlowDevis</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /APERÇU */}
                </div>
            </div>

            {/* ===== BARRE EN BAS (PLACÉE JUSTE SOUS LES BLOCS) ===== */}
            <div className="bg-white rounded-xl border px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    {/* Sélecteurs collés (segmented) */}
                    <div className="inline-flex rounded-full overflow-hidden border border-slate-300">
                        {[
                            { id: "devis", label: "Envoi de devis" },
                            { id: "facture", label: "Envoi de facture" },
                            { id: "relance_devis", label: "Relance de devis" },
                            { id: "relance_impaye", label: "Relance impayé" },
                            { id: "facture_acompte", label: "Acompte" },
                            { id: "facture_partielle", label: "Partielle" },
                            { id: "facture_finale", label: "Finale" },
                        ].map((t, i, arr) => {
                            const isActive = active === t.id;
                            const common =
                                "px-3 h-9 text-sm whitespace-nowrap border-l border-slate-300 first:border-l-0";
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setActive(t.id)}
                                    aria-pressed={isActive}
                                    className={[
                                        common,
                                        isActive ? "bg-gray-300 text-black" : "bg-white text-slate-900 hover:bg-slate-100",
                                    ].join(" ")}
                                >
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Actions collées (segmented) */}
                    <div className="inline-flex rounded-full overflow-hidden border border-slate-300">
                        <button
                            type="button"
                            onClick={() => setShowRestore(true)}
                            className="px-3 h-9 text-sm bg-white text-slate-900 hover:bg-slate-100"
                            title="Restaurer le sujet et le contenu par défaut du modèle actif"
                        >
                            Restaurer le modèle
                        </button>
                        <button
                            type="button"
                            onClick={() => saveRef.current()}
                            disabled={saving}
                            className="px-4 h-9 text-sm bg-sky-700 text-white hover:bg-sky-600 disabled:opacity-60 border-l border-slate-300"
                        >
                            {saving ? "Enregistrement…" : "Enregistrer"}
                        </button>
                    </div>
                </div>
            </div>
            {/* Modale RESTAURER */}
            <ConfirmRestoreModal
                open={showRestore}
                onCancel={() => setShowRestore(false)}
                onConfirm={() => {
                    setShowRestore(false);
                    setCurrent({ subject: DEFAULTS[active].subject, html: DEFAULTS[active].html });
                }}
            />



            {/* ===== /BARRE EN BAS ===== */}
        </div>
    );
}
